import React from "react";
import { motion } from "framer-motion";
import { useLanguage } from "../contexts/LanguageContext";

export default function About() {
  const { t, lang } = useLanguage();

  const processSteps = lang === "de" ? [
    { step: "01", title: "Design & Konzept", desc: "Jedes Werk beginnt mit einer Idee. Ich entwickle digitale 3D-Modelle mit Fokus auf Form, Funktion und ästhetische Wirkung." },
    { step: "02", title: "Prototyping", desc: "Erste Drucke testen Proportionen und Materialverhalten. Jeder Parameter wird präzise abgestimmt." },
    { step: "03", title: "Fertigung", desc: "Der finale Druck erfolgt mit premium Filamenten auf kalibrierten Druckern für höchste Oberflächenqualität." },
    { step: "04", title: "Finishing", desc: "Jedes Objekt wird manuell nachbearbeitet, poliert und auf Qualität geprüft, bevor es sein Zuhause findet." },
  ] : [
    { step: "01", title: "Design & Concept", desc: "Every work begins with an idea. I develop digital 3D models focused on form, function and aesthetic impact." },
    { step: "02", title: "Prototyping", desc: "First prints test proportions and material behavior. Every parameter is precisely calibrated." },
    { step: "03", title: "Manufacturing", desc: "The final print uses premium filaments on calibrated printers for highest surface quality." },
    { step: "04", title: "Finishing", desc: "Each object is manually post-processed, polished and quality-checked before finding its home." },
  ];

  return (
    <div className="min-h-screen bg-background pt-28">
      <div className="max-w-5xl mx-auto px-6">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          className="py-20"
        >
          <span className="text-[11px] font-mono tracking-[0.3em] uppercase text-foreground/30 mb-4 block">
            {t("about.subtitle")}
          </span>
          <h1 className="text-5xl sm:text-6xl font-heading font-bold tracking-tighter text-foreground mb-8 leading-tight">
            {t("about.title")}
          </h1>
          <div className="w-px h-24 bg-gradient-to-b from-foreground/20 to-transparent" />
        </motion.div>

        {/* Story */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="grid grid-cols-1 lg:grid-cols-2 gap-16 mb-24"
        >
          <div>
            <h2 className="text-3xl font-heading font-bold tracking-tighter text-foreground mb-6">{t("about.story.title")}</h2>
            <div className="space-y-4 text-foreground/60 leading-relaxed">
              {lang === "de" ? (
                <>
                  <p>Alles hat mit meinem Abschlussprojekt angefangen. Am Anfang lag der Fokus auf dem T-Shirt-Druck, ich fand aber schnell heraus, dass ich mit meinem 3D-Drucker viel mehr erschaffen kann, als nur T-Shirts zu bedrucken.</p>
                  <p>Jedes Objekt, das ich verkaufe, ist ein Experiment: Wie minimalistisch und präzise kann ich drucken? Wie weit können Formen gehen?</p>
                  <p>Meine Werke im Showroom sind keine Massenware – sie sind einzigartig, mit Liebe zum Detail gefertigt und personalisierbar.</p>
                </>
              ) : (
                <>
                  <p>It all started with my graduation project. Initially, the focus was on printing t-shirts, but I quickly realized that I could create so much more with my 3D printer.</p>
                  <p>Every object I sell is an experiment: How minimalist and precise can 3D printing be? How far can forms go?</p>
                  <p>My works in the showroom are not mass-produced – they are unique, crafted with attention to detail, and customizable.</p>
                </>
              )}
            </div>
          </div>

          <div className="relative">
            <div
              className="aspect-[3/4] bg-cover bg-center rounded-sm overflow-hidden border border-border"
              style={{ backgroundImage: "url(https://images.unsplash.com/photo-1769628628044-769bff843c17?w=800&q=80)" }}
            />
          </div>
        </motion.div>

        {/* Process */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mb-24"
        >
          <span className="text-[11px] font-mono tracking-[0.3em] uppercase text-foreground/30 mb-4 block">
            {t("about.process.desc")}
          </span>
          <h2 className="text-3xl font-heading font-bold tracking-tighter text-foreground mb-12">{t("about.process.title")}</h2>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {processSteps.map((step, i) => (
              <motion.div
                key={step.step}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="p-6 bg-surface border border-border rounded-sm"
              >
                <p className="text-2xl font-mono font-light text-foreground/20 mb-4">{step.step}</p>
                <h3 className="text-sm font-heading font-medium text-foreground mb-3">{step.title}</h3>
                <p className="text-xs text-foreground/50 leading-relaxed">{step.desc}</p>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Philosophy */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="py-20 border-t border-border mb-24"
        >
          <h2 className="text-4xl font-heading font-bold tracking-tighter text-foreground mb-6 max-w-lg">
            {t("about.philosophy.title")}
          </h2>
          <p className="text-lg text-foreground/50 max-w-2xl leading-relaxed">{t("about.philosophy.desc")}</p>
        </motion.div>
        <div className="pb-24" />
      </div>
    </div>
  );
}
